'use client';
import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/i18n/client';
import { THEME_MAP } from '@/[lang]/constant';
import { ActivityStatus } from '@/services/seckill/util';

export interface SeckillCountdownProps {
  lang: string;
  theme: string;
  status: ActivityStatus;
  startTime: number; // Unix 秒
  endTime: number; // Unix 秒
  className?: string;
  onSeckillStart?: (timeLeftSec: number) => void; // 秒杀开始回调 --开抢
  onSeckillEnd?: () => void; // 秒杀结束回调
}

/**
 * 秒杀倒计时：按 status 展示
 * - UPCOMING/REVEALING：即將開搶 + 时/分/秒（青色）
 * - ACTIVE：尚有 + 分/秒/毫秒（红色）
 */
export const SeckillCountdown: React.FC<SeckillCountdownProps> = ({
  lang,
  theme,
  status,
  startTime,
  endTime,
  className = '',
  onSeckillStart,
  onSeckillEnd,
}) => {
  const { t } = useTranslation(lang);
  /** 抢购中 */
  const isActive = status === ActivityStatus.ACTIVE;
  /** 即将开始或预热中或未开始 */
  const isComingOrRevealing = status === ActivityStatus.UPCOMING || status === ActivityStatus.REVEALING || status === ActivityStatus.NOSTART;

  // ACTIVE：剩余时间到 endTime（秒），需毫秒精度
  const endTimeMs = endTime * 1000;
  const startTimeMs = startTime * 1000;
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, endTimeMs - Date.now())
  );

  // UPCOMING/REVEALING：剩余秒数（到 startTime 开抢）
  const [timeLeftSec, setTimeLeftSec] = useState(() =>
    Math.max(0, startTimeMs - Date.now())
  );

  useEffect(() => {
    if (isActive) {
      const timer = setInterval(() => {
        const newRemainingMs = Math.max(0, endTimeMs - Date.now());
        setRemainingMs(newRemainingMs);
        if (newRemainingMs <= 0) {
          onSeckillEnd?.();
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }
  }, [isActive, endTimeMs, onSeckillEnd]);

  useEffect(() => {
    if (!isComingOrRevealing) return;
    const timer = setInterval(() => {
      const newTimeLeftSec = Math.max(0, startTimeMs - Date.now());
      setTimeLeftSec(newTimeLeftSec);
      onSeckillStart?.(newTimeLeftSec);
      if (newTimeLeftSec <= 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isComingOrRevealing, timeLeftSec, startTimeMs, onSeckillStart]);

  const gray = 'text-[13px] text-[#4F4F4F]';
  const teal = theme === THEME_MAP.TJ ? 'text-[#125055]' : 'text-[#E60020]';
  const red = theme === THEME_MAP.TJ ? 'text-[#E10019]' : 'text-[#E3680F]';

  if (status === ActivityStatus.NOSTART || status === ActivityStatus.ENDED) {
    return null;
  }
  // ACTIVE：尚有 59分59秒90毫秒（红色）
  if (isActive) {
    const totalSec = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    const ms = Math.floor((remainingMs % 1000) / 10); // 2 位毫秒（如 90）
    return (
      <div className={`flex items-center justify-center gap-x-1 ${className}`}>
        <span className={gray}>{t('seckill.countdown.remaining')}</span>
        <span className={`font-bold text-[25px] ${red}`}>
          {minutes.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.minute')}</span>
        <span className={`font-bold text-[25px] ${red}`}>
          {seconds.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.second')}</span>
        <span className={`font-bold text-[25px] ${red}`}>
          {ms.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.millisecond')}</span>
      </div>
    );
  }

  // UPCOMING/REVEALING：即將開搶 + 时/分/秒（青色）
  if (isComingOrRevealing) {
    const totalSec = Math.floor(timeLeftSec / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    return (
      <div className={`flex items-center justify-center ${className} gap-x-0.5`}>
        <span className={`${gray} mr-0.5`}>{t('seckill.countdown.remaining')}</span>
        <span className={`font-bold text-[25px] ${teal}`}>
          {hours.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.hour')}</span>
        <span className={`font-bold text-[25px] ${teal}`}>
          {minutes.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.minute')}</span>
        <span className={`font-bold text-[25px] ${teal}`}>
          {seconds.toString().padStart(2, '0')}
        </span>
        <span className={gray}>{t('seckill.countdown.second')}</span>
        <span className={`${gray} ml-0.5`}>{t('seckill.countdown.start')}</span>
      </div>
    );
  }

  return null;
};
